// ============================================================
// PlateDetail.js
// POST PLATES — a steel plate a post is welded onto, drawn from ABOVE: the
// square plate, four bolt holes near the corners, and the post's circle.
//
// SEVERAL PLATES ON ONE SHEET. An order usually needs more than one post size,
// and the owner does not want a sheet each — so they lay out in a grid, each
// with its own dimensions and quantity.
//
// Nested inside GateSheet's drawing area exactly as GateElevation is: same
// 600x400 viewBox, so the sheet frame, data column and bottom strip are the
// ones the gate drawings already use. This is a new DRAWING, not a new sheet.
//
// Pure black line art, no fills — reproduces on any copier.
// ============================================================

import React from 'react';

// Canvas geometry, matching GateElevation's viewBox so the two nest identically.
const VIEW_W = 600;
const VIEW_H = 400;
const PAD_X = 24;
// Headroom above the top row of squares for an offset plate's BACK caption.
const PAD_TOP = 24;
const PAD_BOTTOM = 14;

const INK = '#000000';
const W_MEMBER = 1;
const W_THIN = 0.6;
// The back edge of an offset plate, drawn heavy so the orientation is part of
// the drawing rather than only part of the caption.
const W_BACK_EDGE = 2.2;

// ── HOW MANY FIT ──
//
// SIX, as three across and two down. At six, each cell is about 180x180 units
// inside a 640-unit-wide drawing area — a plate square of roughly 110 units
// with four holes and four lines of text under it, which is legible on a
// photocopy. Eight (four across) puts the cell at 135 and the text starts
// colliding with the square; nine makes the rows too short for four lines.
//
// Beyond six the FORM refuses to add another and asks for a second drawing,
// rather than shrinking the grid until nothing can be read. A second drawing
// gets its own number, its own sheet and prints alongside the first.
export const MAX_PLATES_PER_SHEET = 6;
const COLUMNS = 3;

// ════════════════════ THE VOCABULARIES ════════════════════
//
// What the shop actually stocks, and nothing else. ONE definition each: the
// form offers these and the drawing labels from them, so an addition cannot
// land in one and be missing from the other.
//
// Each entry is { value, label } — the VALUE is the number the drawing scales
// from and Firestore stores; the LABEL is what the shop calls it.

/** Post outside diameter, in inches. */
export const POST_OD_OPTIONS = [
  { value: 1.625, label: '1-5/8"' },
  { value: 2, label: '2"' },
  { value: 2.5, label: '2-1/2"' },
  { value: 3, label: '3"' },
  { value: 4, label: '4"' },
  { value: 6.625, label: '6-5/8"' },
];

/** Plate size — always square, so one number is the side. */
export const PLATE_SIZE_OPTIONS = [
  { value: 4, label: '4x4' },
  { value: 6, label: '6x6' },
  { value: 8, label: '8x8' },
];

/** Plate thickness, in inches. 1/4" is the default. */
export const THICKNESS_OPTIONS = [
  { value: 0.25, label: '1/4"' },
  { value: 0.375, label: '3/8"' },
];

const labelFrom = (options, value, fallback) => {
  const v = parseFloat(value);
  const match = options.find(o => Math.abs(o.value - v) < 0.001);
  return match ? match.label : fallback(Number.isFinite(v) ? v : 0);
};

export const postOdLabel = (v) => labelFrom(POST_OD_OPTIONS, v, (n) => `${n}"`);
export const plateSizeLabel = (v) => labelFrom(PLATE_SIZE_OPTIONS, v, (n) => `${n}x${n}`);
export const thicknessLabel = (v) => labelFrom(THICKNESS_OPTIONS, v, (n) => `${n}"`);

/**
 * The options a SELECT should offer for a stored value.
 *
 * A drawing saved before these lists were fixed may hold a size, thickness or
 * diameter that is no longer stocked. That value is NOT discarded and NOT
 * silently snapped to the nearest stocked one — either would rewrite a record
 * of what was actually built. It is added to the list, marked "(saved)", and
 * selected; the user sees exactly what is stored and can re-pick when they want
 * to change it.
 */
export function optionsFor(options, storedValue, labelFn) {
  const v = parseFloat(storedValue);
  if (!Number.isFinite(v) || options.some(o => Math.abs(o.value - v) < 0.001)) return options;
  return [...options, { value: v, label: `${labelFn(v)} (saved)` }];
}

// ── WHERE THE POST SITS ──
//
// Two positions. "Offset" means centred left to right and shifted BACK, toward
// one edge — never toward a corner. The four corner positions this replaced are
// gone; a drawing that stored one maps to Offset (see normalizePlatePosition),
// because that is what those drawings meant: a post that is not in the middle.
export const PLATE_POSITIONS = ['Centered', 'Offset'];

/**
 * The stored position, as one of the two that now exist.
 *
 * Nothing stored — every drawing made before this field — is Centered, which is
 * the ordinary plate. Anything else, including 'Top left' and the other three
 * retired corners, is Offset.
 */
export function normalizePlatePosition(position) {
  const p = String(position ?? '').trim();
  if (!p || p === 'Centered') return 'Centered';
  return 'Offset';
}

// ── POST LENGTH: FREE TEXT ──
//
// A real post length is 10'6" — a foot mark AND an inch mark in one value — and
// the shop also writes 8', 96", 7'-3" and "10 ft 6 in". A numeric field cannot
// hold any of those, so this one is plain text: whatever is typed is stored and
// printed VERBATIM. No pattern, no stripping of quotes, no reformatting on
// blur. The only change ever made to it is a trim of the outside whitespace,
// and that happens on save.
//
// A double quote inside the value is safe in both places it appears: in the
// drawing it is a TEXT NODE, and in the aria-label it is an attribute React
// escapes on the way out — neither can break the SVG.

// ── THE CAP ──
// 24 characters. The longest real answer anyone has needed is about half that
// ("10 ft 6 in" is 10, "7'-3"" is 5), and the label sits in a cell about 160
// units wide beside five other lines — a pasted paragraph would push the text
// across its neighbours and off the sheet.
//
// The input stops at 24 characters, so past the cap the typing simply does not
// go in; nothing is silently rewritten as the user types. The drawing slices to
// the same 24 as a backstop, for a value that arrived some other way (an older
// record, a paste into the console).
export const POST_LENGTH_MAX = 24;

/**
 * The length to print for a plate, or '' when none is recorded.
 *
 * Reads the free-text field first. Falls back to the NUMERIC feet field the
 * first version of this feature wrote, so a drawing saved under it still shows
 * its length instead of losing it; opening that drawing in the form migrates
 * the value into the text field (see plateForForm).
 */
export function postLengthText(plate) {
  const raw = plate?.postLength;
  if (typeof raw === 'string' && raw.trim()) return raw.trim().slice(0, POST_LENGTH_MAX);
  return legacyFeetLabel(plate?.postLengthFt);
}

/** The retired numeric field, formatted as it used to print: 8.5 → 8'6". */
export function legacyFeetLabel(feet) {
  const ft = parseFloat(feet) || 0;
  if (ft <= 0) return '';
  const whole = Math.floor(ft);
  const inches = Math.round((ft - whole) * 12);
  if (inches === 0) return `${whole}'`;
  if (inches === 12) return `${whole + 1}'`;
  return `${whole}'${inches}"`;
}

/**
 * A stored plate as the FORM should hold it: the free-text length in place,
 * migrated from the retired numeric field when that is all there is. Applied
 * when a drawing is opened, so editing and re-saving cannot drop a length that
 * was recorded under the old field.
 */
export const plateForForm = (plate) => ({
  ...plate,
  postLength: (typeof plate?.postLength === 'string' && plate.postLength !== '')
    ? plate.postLength
    : legacyFeetLabel(plate?.postLengthFt),
});

/** A new plate's defaults — one place, so the form and a paste agree. */
export const BLANK_PLATE = {
  plateSizeIn: 8,
  thicknessIn: 0.25,      // 1/4", the default the owner asked for
  postOdIn: 2,
  postLength: '',
  position: 'Centered',
  quantity: 1,
};

// A number, or 0 for anything unparseable.
const num = (v) => parseFloat(v) || 0;

/**
 * The drawn plate size, as a fraction of the biggest plate on the sheet.
 *
 * Plates of different sizes MUST look different — a 6" plate beside a 12" one
 * reads as half the square, which is the whole point of drawing them together.
 * The floor stops that honesty from making a small plate illegible next to a
 * very large one: below 45% of the biggest, the square stops shrinking. Two
 * plates within that range are still ordered by size on the page, and every
 * plate carries its own figures in text underneath regardless.
 */
const MIN_RELATIVE_SIZE = 0.45;
const relativeSize = (sizeIn, maxIn) => {
  if (!(maxIn > 0)) return MIN_RELATIVE_SIZE;
  return Math.max(MIN_RELATIVE_SIZE, Math.min(1, num(sizeIn) / maxIn));
};

/**
 * One plate, drawn from above inside its cell.
 *
 * `span` is the cell's square drawing zone; the plate fills `span` × its
 * relative size. The post circle is scaled from THIS plate's own size, so the
 * ratio of post to plate is true even though the plates share a page.
 */
function Plate({ plate, cx, top, span, maxIn, hideDimensions }) {
  const plateIn = num(plate?.plateSizeIn);
  const postIn = num(plate?.postOdIn);
  const side = span * relativeSize(plateIn, maxIn);
  const x = cx - side / 2;
  const y = top + (span - side) / 2;

  // The post, proportional to its own plate. Clamped so a nonsense entry (a
  // post wider than its plate) still draws something inside the square.
  const postD = plateIn > 0 ? Math.min(side * 0.92, side * (postIn / plateIn)) : side * 0.3;
  const postR = postD / 2;

  // Bolt holes: four, near the corners, at a twelfth of the plate in from each
  // edge. Drawn as circles, not crosses — this is a plan view.
  const holeInset = side * 0.12;
  const holeR = Math.max(2, side * 0.045);
  const holes = [
    [x + holeInset, y + holeInset], [x + side - holeInset, y + holeInset],
    [x + holeInset, y + side - holeInset], [x + side - holeInset, y + side - holeInset],
  ];

  // ── WHERE THE POST SITS ──
  //
  // Centered is the middle. OFFSET is centred left to right and shifted BACK:
  // toward the top edge in this plan view, never toward a corner. The margin
  // clears the bolt hole and leaves room to weld all the way round.
  //
  // MAKING IT UNMISTAKABLE. A circle a little off centre reads as a sloppy
  // drawing, not as a specification — so the drawing says it three ways:
  //   • the back edge is drawn HEAVY and captioned BACK, so the sheet states
  //     which edge the post is shifted toward rather than leaving it to be
  //     inferred from an unlabelled square;
  //   • a dashed centre line runs across the plate, so the displacement is
  //     visible as a gap against a reference rather than as a vague feeling;
  //   • the text under the plate says OFFSET — TOWARD BACK EDGE.
  // The offset itself is still NOT dimensioned: the owner confirmed that.
  const pos = normalizePlatePosition(plate?.position);
  const isOffset = pos === 'Offset';
  const backGap = Math.max(side * 0.30, holeInset + holeR + postR + side * 0.04);
  const inset = Math.min(backGap, side - postR - side * 0.04);
  const postCx = x + side / 2;                       // always centred side to side
  const postCy = isOffset ? y + inset : y + side / 2;

  // Text under the square. LABELLED, because a welder must never have to guess
  // which number is the plate and which is the post.
  const textTop = top + span + 12;
  const lengthLabel = postLengthText(plate);
  return (
    <g>
      <rect x={x} y={y} width={side} height={side} fill="none" stroke={INK} strokeWidth={W_MEMBER} />
      {holes.map(([hx, hy], i) => (
        <circle key={i} cx={hx} cy={hy} r={holeR} fill="none" stroke={INK} strokeWidth={W_THIN} />
      ))}
      {/* Offset only: the heavy back edge, its caption, and the centre line the
          post is visibly off. Nothing is added to a centred plate, which is
          symmetrical and has no back. */}
      {isOffset && (
        <>
          <line x1={x} y1={y} x2={x + side} y2={y} stroke={INK} strokeWidth={W_BACK_EDGE} />
          <text x={cx} y={y - 5} fill={INK} fontSize="8" fontWeight="700" letterSpacing="1.5" textAnchor="middle">
            BACK
          </text>
          <line
            x1={x + side * 0.08} y1={y + side / 2} x2={x + side * 0.92} y2={y + side / 2}
            stroke={INK} strokeWidth={W_THIN} strokeDasharray="4 3"
          />
        </>
      )}
      <circle cx={postCx} cy={postCy} r={postR} fill="none" stroke={INK} strokeWidth={W_MEMBER} />
      {!hideDimensions && (
        <>
          <text x={cx} y={textTop} fill={INK} fontSize="11" fontWeight="700" textAnchor="middle">
            PLATE {plateSizeLabel(plateIn)}
          </text>
          <text x={cx} y={textTop + 13} fill={INK} fontSize="10" textAnchor="middle">
            THICKNESS {thicknessLabel(plate?.thicknessIn)}
          </text>
          {/* Diameter AND length together: the welder needs both to pick the
              post off the rack. */}
          <text x={cx} y={textTop + 26} fill={INK} fontSize="10" textAnchor="middle">
            POST {postOdLabel(postIn)}{lengthLabel ? ` × ${lengthLabel}` : ''}
          </text>
          <text x={cx} y={textTop + 39} fill={INK} fontSize="11" fontWeight="700" textAnchor="middle">
            QTY {Math.max(1, Math.round(num(plate?.quantity) || 1))}
          </text>
          {isOffset && (
            <text x={cx} y={textTop + 52} fill={INK} fontSize="10" fontWeight="700" textAnchor="middle">
              OFFSET — TOWARD BACK EDGE
            </text>
          )}
        </>
      )}
    </g>
  );
}

/**
 * The grid of plates.
 *
 * `hideDimensions` (a blank sheet) draws the squares, holes and post circles
 * and drops every number, so the shop can write the sizes on by hand — the same
 * meaning it has on a gate sheet.
 */
export default function PlateDetail({ plates, x, y, width, height, hideDimensions = false }) {
  const list = (Array.isArray(plates) ? plates : []).slice(0, MAX_PLATES_PER_SHEET);
  const maxIn = list.reduce((m, p) => Math.max(m, num(p?.plateSizeIn)), 0);

  const columns = Math.max(1, Math.min(COLUMNS, list.length || 1));
  const rows = Math.max(1, Math.ceil(list.length / columns));
  const cellW = (VIEW_W - PAD_X * 2) / columns;
  const cellH = (VIEW_H - PAD_TOP - PAD_BOTTOM) / rows;
  // The square's drawing zone inside a cell: the rest of the row is the four
  // text lines beneath it (about 52 units), plus a gutter between columns.
  // Room under each square for its labels: four lines always, plus a fifth on
  // an offset plate. At six plates this leaves a square of about 110 units —
  // see MAX_PLATES_PER_SHEET, which was re-checked against this taller block.
  const textRoom = hideDimensions ? 10 : 70;
  const span = Math.max(20, Math.min(cellW - 24, cellH - textRoom));

  return (
    <svg
      x={x} y={y} width={width} height={height}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      role="img"
      aria-label={
        list.length === 0
          ? 'Post plate drawing with no plates yet'
          : `Post plate drawing, ${list.length} plate${list.length === 1 ? '' : 's'}: ` +
            list.map(p => {
              const len = postLengthText(p);
              return `${plateSizeLabel(p?.plateSizeIn)} plate ${thicknessLabel(p?.thicknessIn)} thick, ` +
                `${postOdLabel(p?.postOdIn)} post${len ? ` ${len} long` : ''}` +
                `${normalizePlatePosition(p?.position) === 'Offset' ? ', offset toward the back edge' : ', centred'}` +
                `, quantity ${Math.max(1, Math.round(num(p?.quantity) || 1))}`;
            }).join('; ') + '.'
      }
      style={{ display: 'block', fontFamily: 'Manrope, sans-serif' }}
    >
      {list.length === 0 ? (
        <text x={VIEW_W / 2} y={VIEW_H / 2} fill={INK} fontSize="13" textAnchor="middle">
          No plates added yet
        </text>
      ) : list.map((plate, i) => {
        const col = i % columns;
        const row = Math.floor(i / columns);
        // Each cell's own centre line, and the top of its square zone.
        const cx = PAD_X + cellW * col + cellW / 2;
        const top = PAD_TOP + cellH * row + Math.max(0, (cellH - textRoom - span) / 2);
        return (
          <Plate
            key={plate?.id ?? i}
            plate={plate}
            cx={cx}
            top={top}
            span={span}
            maxIn={maxIn}
            hideDimensions={hideDimensions}
          />
        );
      })}
    </svg>
  );
}
